(function () {
  console.log("🎁 GiftEngine v7 init");

  if (!window.GIFT_ENGINE_CONFIG) {
    console.warn("GiftEngine config missing");
    return;
  }

  const config = window.GIFT_ENGINE_CONFIG;
  const MILESTONE_MODE = !!config.milestoneEnabled;
  const ALLOW_MULTI_TIER = !!config.allowMultiTierGifts;
  const AUTO_ADD = !!config.giftAutoAdd;

  // Legacy single-gift attribute
  const FREE_ATTR = "_free_gift";
  const FREE_VALUE = "true";

  // Milestone attributes
  const M1_ATTR = "_free_gift_m1";
  const M2_ATTR = "_free_gift_m2";
  const MILESTONE_ATTRS = [M1_ATTR, M2_ATTR];

  let running = false;
  let addingGift = false;
  let lastCartHash = null;

  function hashCart(cart) {
    return (cart.items || [])
      .map((item) => {
        return [
          item.key,
          item.variant_id,
          item.quantity,
          item.properties?.[FREE_ATTR] || "",
          item.properties?.[M1_ATTR] || "",
          item.properties?.[M2_ATTR] || "",
          item.final_line_price || 0
        ].join(":");
      })
      .join("|");
  }

  // --- Detection helpers ---

  function isLegacyFreeGiftLine(item) {
    return item?.properties?.[FREE_ATTR] === FREE_VALUE;
  }

  function isMilestoneFreeGiftLine(item, milestoneIndex) {
    const attr = MILESTONE_ATTRS[milestoneIndex];
    return item?.properties?.[attr] === FREE_VALUE;
  }

  function isAnyFreeGiftLine(item) {
    if (isLegacyFreeGiftLine(item)) return true;
    return MILESTONE_ATTRS.some((attr) => item?.properties?.[attr] === FREE_VALUE);
  }

  function getMilestoneGiftLines(cart, milestoneIndex) {
    return (cart.items || []).filter((item) => isMilestoneFreeGiftLine(item, milestoneIndex));
  }

  function getQualifyingSubtotalCents(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (isAnyFreeGiftLine(item)) return sum;
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  function thresholdReached(cart) {
    return getQualifyingSubtotalCents(cart) >= Math.round(Number(config.threshold || 0) * 100);
  }

  function milestoneThresholdReached(cart, milestoneIndex) {
    const milestone = config.milestones?.[milestoneIndex];
    if (!milestone || !milestone.threshold) return false;
    return getQualifyingSubtotalCents(cart) >= Math.round(Number(milestone.threshold) * 100);
  }

  // --- Fetch helpers ---

  async function fetchJSON(url, options = {}) {
    const headers = { ...(options.headers || {}) };

    if (options.method && options.method !== "GET" && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    const res = await fetch(url, {
      credentials: "same-origin",
      ...options,
      headers
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Request failed: ${res.status} ${text}`);
    }

    return res.json();
  }

  async function changeLine(key, quantity) {
    return fetchJSON("/cart/change.js", {
      method: "POST",
      body: JSON.stringify({ id: key, quantity })
    });
  }

  async function addGiftVariant(variantId, attr) {
    return fetchJSON("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [attr]: FREE_VALUE
            }
          }
        ]
      })
    });
  }

  // Adds one line per variant. Sequential on purpose: a single /cart/add.js
  // call with several items fails as a whole when one gift is out of stock.
  async function addGiftVariants(variantIds, attr) {
    let added = 0;
    for (const variantId of variantIds) {
      try {
        await addGiftVariant(variantId, attr);
        added += 1;
      } catch (error) {
        console.warn("GiftEngine could not add gift variant", variantId, error);
      }
    }
    return added;
  }

  async function addPaidVariant(variantId, quantity = 1) {
    return fetchJSON("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        items: [{ id: variantId, quantity }]
      })
    });
  }

  async function getCart() {
    return fetchJSON("/cart.js", { method: "GET" });
  }

  async function refreshCartEvents(cart) {
    window.GA_CART = cart;
    window.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
    window.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  // --- Normalize: Legacy mode (one free line per configured gift product) ---

  async function normalizeLegacy(cart) {
    const gifts = (cart.items || []).filter(isLegacyFreeGiftLine);
    const qualified = thresholdReached(cart);

    if (!qualified) {
      if (gifts.length > 0) {
        for (const item of gifts) {
          await changeLine(item.key, 0);
        }
        const freshCart = await getCart();
        lastCartHash = hashCart(freshCart);
        await refreshCartEvents(freshCart);
      }
      return;
    }

    // One free line per configured gift product. Anything else carrying the
    // gift attribute (duplicate line, product removed from the metafield) goes.
    const allowedVariantIds = giftVariantIds(config.giftProducts);
    const keptVariantIds = new Set();
    let mutated = false;

    for (const item of gifts) {
      const allowed =
        allowedVariantIds.includes(item.variant_id) && !keptVariantIds.has(item.variant_id);
      const nextQty = allowed ? Math.min(item.quantity, 1) : 0;
      if (nextQty !== item.quantity) {
        mutated = true;
        await changeLine(item.key, nextQty);
      }
      if (nextQty > 0) keptVariantIds.add(item.variant_id);
    }

    if (mutated) {
      const freshCart = await getCart();
      lastCartHash = hashCart(freshCart);
      await refreshCartEvents(freshCart);
    }
  }

  // --- Normalize: Milestone mode ---

  function getAllMilestoneGiftLines(cart) {
    return (cart.items || []).filter((item) =>
      MILESTONE_ATTRS.some((attr) => item?.properties?.[attr] === FREE_VALUE)
    );
  }

  async function normalizeMilestones(cart) {
    let mutated = false;

    // Remove gifts from milestones that are no longer qualified
    for (let mi = 0; mi < MILESTONE_ATTRS.length; mi++) {
      const qualified = milestoneThresholdReached(cart, mi);
      if (!qualified) {
        const gifts = getMilestoneGiftLines(cart, mi);
        if (gifts.length > 0) {
          for (const item of gifts) {
            await changeLine(item.key, 0);
          }
          mutated = true;
        }
      }
    }

    if (ALLOW_MULTI_TIER) {
      // Enforce max 1 free gift per milestone
      const freshCart = mutated ? await getCart() : cart;
      for (let mi = 0; mi < MILESTONE_ATTRS.length; mi++) {
        const gifts = getMilestoneGiftLines(freshCart, mi);
        let kept = 0;
        for (const item of gifts) {
          const nextQty = kept >= 1 ? 0 : Math.min(item.quantity, 1);
          if (nextQty !== item.quantity) {
            mutated = true;
            await changeLine(item.key, nextQty);
          }
          kept += nextQty;
        }
      }
    } else {
      // Enforce max 1 free gift total across all milestones
      const allGifts = getAllMilestoneGiftLines(mutated ? await getCart() : cart);
      let totalFreeKept = 0;
      for (const item of allGifts) {
        const nextQty = totalFreeKept >= 1 ? 0 : Math.min(item.quantity, 1);
        if (nextQty !== item.quantity) {
          mutated = true;
          await changeLine(item.key, nextQty);
        }
        totalFreeKept += nextQty;
      }
    }

    if (mutated) {
      const freshCart = await getCart();
      lastCartHash = hashCart(freshCart);
      await refreshCartEvents(freshCart);
    }
  }

  // --- Auto-add helpers ---

  function giftVariantIds(productList) {
    if (!Array.isArray(productList)) return [];
    const ids = [];
    for (const p of productList) {
      if (p && p.variantId && !ids.includes(p.variantId)) ids.push(p.variantId);
    }
    return ids;
  }

  function firstAvailableVariantId(productList) {
    return giftVariantIds(productList)[0] || null;
  }

  async function autoAddLegacy(cart) {
    if (!thresholdReached(cart)) return false;

    const variantIds = giftVariantIds(config.giftProducts);
    if (variantIds.length === 0) return false;

    const present = new Set(
      (cart.items || []).filter(isLegacyFreeGiftLine).map((item) => item.variant_id)
    );
    const missing = variantIds.filter((id) => !present.has(id));
    if (missing.length === 0) return false;

    const added = await addGiftVariants(missing, FREE_ATTR);
    return added > 0;
  }

  async function autoAddMilestones(cart) {
    let added = false;
    let currentCart = cart;

    if (ALLOW_MULTI_TIER) {
      for (let mi = 0; mi < MILESTONE_ATTRS.length; mi++) {
        if (!milestoneThresholdReached(currentCart, mi)) continue;
        if (getMilestoneGiftLines(currentCart, mi).length > 0) continue;

        const variantId = firstAvailableVariantId(config.milestones?.[mi]?.products);
        if (!variantId) continue;

        await addGiftVariant(variantId, MILESTONE_ATTRS[mi]);
        added = true;
        currentCart = await getCart();
      }
    } else {
      if (getAllMilestoneGiftLines(currentCart).length > 0) return false;

      // Pick the highest qualified tier
      for (let mi = MILESTONE_ATTRS.length - 1; mi >= 0; mi--) {
        if (!milestoneThresholdReached(currentCart, mi)) continue;

        const variantId = firstAvailableVariantId(config.milestones?.[mi]?.products);
        if (!variantId) continue;

        await addGiftVariant(variantId, MILESTONE_ATTRS[mi]);
        added = true;
        break;
      }
    }

    return added;
  }

  // --- Main normalize entry ---

  async function normalizeCart(cart) {
    if (running) return;
    running = true;

    try {
      const currentHash = hashCart(cart);
      if (currentHash === lastCartHash) {
        running = false;
        return;
      }
      lastCartHash = currentHash;

      if (!config.enabled) {
        running = false;
        return;
      }

      if (MILESTONE_MODE) {
        await normalizeMilestones(cart);
      } else {
        await normalizeLegacy(cart);
      }

      if (AUTO_ADD) {
        const latestCart = await getCart();
        const added = MILESTONE_MODE
          ? await autoAddMilestones(latestCart)
          : await autoAddLegacy(latestCart);

        if (added) {
          const freshCart = await getCart();
          lastCartHash = hashCart(freshCart);
          await refreshCartEvents(freshCart);
        }
      }
    } catch (error) {
      console.error("GiftEngine normalizeCart error", error);
    }

    running = false;
  }

  // --- Public API ---

  window.GiftEngine = {
    // Legacy: addGift(variantId) — single gift mode
    // Milestone: addGift(variantId, milestoneIndex) — milestone mode
    async addGift(variantId, milestoneIndex) {
      if (addingGift) return;
      addingGift = true;

      try {
        const cart = await getCart();

        if (MILESTONE_MODE && milestoneIndex !== undefined) {
          if (!milestoneThresholdReached(cart, milestoneIndex)) {
            console.warn("Milestone threshold not reached for M" + (milestoneIndex + 1));
            return;
          }

          if (ALLOW_MULTI_TIER) {
            const tierGifts = getMilestoneGiftLines(cart, milestoneIndex);
            if (tierGifts.length > 0) {
              console.warn("Already have a free gift for M" + (milestoneIndex + 1));
              return;
            }
          } else {
            const allGifts = getAllMilestoneGiftLines(cart);
            if (allGifts.length > 0) {
              console.warn("Already have a free gift in cart");
              return;
            }
          }

          const attr = MILESTONE_ATTRS[milestoneIndex];
          await addGiftVariant(variantId, attr);
        } else {
          // Legacy mode
          if (!thresholdReached(cart)) {
            console.warn("Gift threshold not reached");
            return;
          }

          const alreadyAdded = (cart.items || []).some(
            (item) => isLegacyFreeGiftLine(item) && item.variant_id === variantId
          );
          if (alreadyAdded) {
            console.warn("This free gift is already in the cart");
            return;
          }

          await addGiftVariant(variantId, FREE_ATTR);
        }

        const freshCart = await getCart();
        await normalizeCart(freshCart);
        await refreshCartEvents(freshCart);
      } catch (error) {
        console.error("GiftEngine addGift error", error);
      } finally {
        addingGift = false;
      }
    },

    async addPaidCopyOfGiftVariant(variantId) {
      try {
        await addPaidVariant(variantId, 1);
        const freshCart = await getCart();
        await normalizeCart(freshCart);
        await refreshCartEvents(freshCart);
      } catch (error) {
        console.error("GiftEngine addPaidCopyOfGiftVariant error", error);
      }
    },

    async sync() {
      try {
        const cart = await getCart();
        await normalizeCart(cart);
      } catch (error) {
        console.error("GiftEngine sync error", error);
      }
    },

    isQualified(cart) {
      return thresholdReached(cart);
    },

    isMilestoneQualified(cart, milestoneIndex) {
      return milestoneThresholdReached(cart, milestoneIndex);
    },

    qualifyingSubtotalCents(cart) {
      return getQualifyingSubtotalCents(cart);
    },

    isFreeGiftLine: isAnyFreeGiftLine,

    isMilestoneFreeGiftLine,

    get milestoneMode() {
      return MILESTONE_MODE;
    },

    get allowMultiTier() {
      return ALLOW_MULTI_TIER;
    }
  };

  window.addEventListener("cart:updated", async (e) => {
    const cart = e.detail;
    if (!cart) return;
    await normalizeCart(cart);
  });

  document.addEventListener("DOMContentLoaded", async () => {
    try {
      const cart = window.GA_CART || await getCart();
      await normalizeCart(cart);
    } catch (error) {
      console.error("GiftEngine initial sync error", error);
    }
  });
})();
